import imageCompression from 'browser-image-compression';
import SyiMetrics from '@gumtree/web-bff/src/metrics/modules/syi-metrics';
import UploadImageMetrics from '@gumtree/web-bff/src/metrics/modules/upload-image-metrics';
import { getActualImageType } from '@gumtree/shared/src/util/get-image-type';
import { getImageCompressionOptionsV2 } from '@gumtree/shared/src/util/image-compression';
import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';

import Sortable from 'sortablejs';
import * as Sentry from '@sentry/browser';

import {
    getPageStateThunk,
    PlaceholderImageDef,
    SyiImageDef,
    updateSyi,
    updateSyiFormError,
} from '../reducers/syi';
import { dtlSyi, fetchOrThrow } from './dtl-syi';
import { trackEvents } from './tracking';
import { trackV2 } from './common-utils';
import { computeListingDetails } from './syi-model';

const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', '.jpeg', '.jpg', '.png'];

export default class ImageUpload {
    get el() {
        return dtlSyi.form!.el.querySelector('.panel.images ul.images') as HTMLUListElement;
    }
    get mainHiddenInput() {
        return dtlSyi.form!.el.querySelector(
            '.panel.images input[name=mainImageId]'
        ) as HTMLInputElement;
    }
    /** Possibly null if cannot add image */
    get fileInput() {
        return dtlSyi.form!.el.querySelector('.panel.images input[type=file]') as
            | null
            | (HTMLInputElement & {
                  type: 'file';
              });
    }
    get url() {
        return `${dtlSyi.postadURL}/images`;
    }
    get deleteImageUrl() {
        return `${dtlSyi.postadURL}/images/delete`;
    }
    get orderImageUrl() {
        return `${dtlSyi.postadURL}/images/order`;
    }

    options = {
        maximages: 20,
        downsize: {},
    };
    multiUpload = false;
    QUEUE_STATUS = 'IDLE' as QueueStatus;
    uploadQueue = [] as QueueItem[];
    sortable!: Sortable;

    addIdToPlaceholderDef({ id, url }, item: QueueItem) {
        const { imageDefs } = dtlSyi.dispatch(getPageStateThunk()).syi;
        const found = imageDefs.find((def) => 'file' in def && def.file === item.formQuery.image);
        if (found) {
            found.id = id;
            found.url = url;
        }
    }

    addToQueue(data: QueueItem) {
        this.uploadQueue.push(data);
        this.checkQueueStatus();
    }

    checkQueueStatus() {
        if (this.uploadQueue.length > 0 && this.QUEUE_STATUS === 'IDLE') {
            const firstItem = this.uploadQueue.shift();
            this.changeQueueStatus('BUSY');
            firstItem && this.uploadXhr(firstItem);
        }
        return this.QUEUE_STATUS;
    }
    changeQueueStatus(status: QueueStatus) {
        this.QUEUE_STATUS = status;
        this.checkQueueStatus();
    }

    deleteImageFail() {
        this.printError("Sorry, we couldn't delete your image, try again");
    }

    enableSortable(state: boolean) {
        this.sortable.option('disabled', !state);
    }

    async deleteImg(item: SyiImageDef) {
        this.imageXhr({ id: item.id }, 'POST', 'delete')
            .then((resp) => this.deleteImageSuccess(resp as { id: number }))
            .catch(() => this.deleteImageFail());
    }

    deleteImageSuccess(response: { id: number }) {
        const { imageDefs } = dtlSyi.dispatch(getPageStateThunk()).syi;
        const found = imageDefs.find((def) => def.id === response.id);

        if (found) {
            // Fade then remove
            found.fade = true;
            dtlSyi.dispatch(updateSyi({ imageDefs: imageDefs.slice() }));
            setTimeout(() => {
                const { imageDefs } = dtlSyi.dispatch(getPageStateThunk()).syi;
                dtlSyi.dispatch(
                    updateSyi({ imageDefs: imageDefs.filter((def) => def.id !== response.id) })
                );
            }, 300);

            trackV2('ImageUploadDelete');
        }
    }

    async fileUpload(files: File[]) {
        const isImageLimitReached = this.isImageLimitReached(files.length);
        this.multiUpload = files.length > 1;

        //Clear previous errors when upload new images
        dtlSyi.dispatch(updateSyiFormError({ elName: 'imageIds[]', errors: [] }));

        //Limit number of images
        if (isImageLimitReached) {
            files = files.slice(0, this.options.maximages - this.shownImageNumber());
        }

        this.enableSortable(files.length === 0);

        let placeholderPosition = this.shownImageNumber() + 1;

        for (const file of files) {
            let resizedBlob = undefined as undefined | Blob;

            const isAllowedType = await this.isAllowedImageType(file);
            if (isAllowedType) {
                const startTime = Date.now();
                const { baseConfig, page, syi } = dtlSyi.dispatch(getPageStateThunk());
                const compressionOptions = getImageCompressionOptionsV2(file, baseConfig.device.os);

                await imageCompression(file, compressionOptions)
                    .then((result) => (resizedBlob = result))
                    .catch((err) => {
                        /** Fallback to no browser-side resize */
                        const errorMessage = this.getErrorMessage(err);
                        const extraLoggingInfo = file
                            ? `type: ${file.type}. size: ${file.size}. name: ${file.name}. error: ${errorMessage}`
                            : `error: ${errorMessage}`;

                        Sentry.withScope((scope) => {
                            scope.setContext('component', { name: 'ImageUpload' });
                            scope.setContext('operation', { type: 'resize' });
                            scope.setContext(
                                'fileInfo',
                                file
                                    ? {
                                          type: file.type,
                                          size: file.size,
                                          name: file.name,
                                      }
                                    : null
                            );
                            scope.setContext('originalError', { error: err });
                            scope.setLevel('error');

                            Sentry.captureException(
                                new Error(
                                    `Error resizing image. Falling back to original. ${extraLoggingInfo}`
                                )
                            );
                        });
                    })
                    .finally(() => {
                        UploadImageMetrics.registerStageDuration({
                            stage: 'compression',
                            durationMs: Date.now() - startTime,
                            os: baseConfig.device.os,
                            device: baseConfig.device.type,
                            appViewDevice: page.appViewDevice,
                            imageType: file.type,
                            imageSizeBytes: file.size,
                            resizedImageSizeBytes: resizedBlob?.size ?? file.size,
                            state: resizedBlob ? 'success' : 'fail',
                        });
                        const newImageDef: PlaceholderImageDef = {
                            file,
                            blobUrl: URL.createObjectURL(resizedBlob || file),
                            waiting: true,
                            startTime,
                        };
                        const { imageDefs } = syi;
                        dtlSyi.dispatch(
                            updateSyi({
                                imageDefs: imageDefs.concat(newImageDef),
                            })
                        );
                        this.startUpload({
                            formQuery: { image: file, resizedBlob, position: placeholderPosition },
                        });
                    });
            } else {
                this.printError(`Image type not supported - please use jpeg, jpg or png`);
                this.changeQueueStatus('IDLE');
            }

            placeholderPosition++;
        }
    }

    getDomImageIds() {
        return Array.from(this.el.querySelectorAll('input[name="imageIds[]"]')).map((el) =>
            Number((el as HTMLInputElement).value)
        );
    }

    /**
     * Used for re-ordering images or deleting images.
     */
    async imageXhr(ajaxData: any, method: 'POST', endpointType: 'delete' | 'reorder') {
        const stringifiedAjaxData = JSON.stringify(ajaxData);

        if (endpointType === 'delete') {
            return (await fetchOrThrow(
                this.deleteImageUrl,
                {
                    method,
                    headers: { 'content-type': 'application/json' },
                    body: stringifiedAjaxData,
                    credentials: 'include',
                },
                {
                    serviceName: 'seller',
                    path: '/postad/:id/images/delete',
                    method: 'POST',
                }
            ).then((x) => x.json())) as { id: number };
        }
        if (endpointType === 'reorder') {
            await fetchOrThrow(
                this.orderImageUrl,
                {
                    method,
                    headers: { 'content-type': 'application/json' },
                    body: stringifiedAjaxData,
                    credentials: 'include',
                },
                {
                    serviceName: 'seller',
                    path: '/postad/:id/images/order',
                    method: 'POST',
                }
            );
        }
        return undefined;
    }

    imageUploadError(_item: QueueItem, error: any) {
        const categoryId = dtlSyi.model.data.categoryId!;
        const { appViewDevice } = dtlSyi.dispatch(getPageStateThunk()).page;

        SyiMetrics.registerImageUploadEvent('fail', categoryId, appViewDevice);

        trackEvents('AddImageFail');
        this.printError(`${error}`);
        this.changeQueueStatus('IDLE');
    }

    imageUploadResponseHandler(item: QueueItem, resp: any) {
        // 🚧 ?
        // var response = JSON.parse(resp.replace(
        //     /^\s*<pre[^>]*>/, ''
        // ).replace(
        //     /<\/pre>\s*$/, ''));
        this.imageUploadSuccess(item, resp);
    }

    imageUploadSuccess(item: QueueItem, resp: any) {
        const categoryId = dtlSyi.model.data.categoryId!;
        const { appViewDevice } = dtlSyi.dispatch(getPageStateThunk()).page;

        SyiMetrics.registerImageUploadEvent('success', categoryId, appViewDevice);

        trackEvents('AddImageSuccess');
        this.addIdToPlaceholderDef({ id: resp.id, url: resp.url }, item);
        this.stopSpinner(item);

        if (!this.multiUpload) {
            this.changeQueueStatus('IDLE');
        }

        this.enableSortable(this.checkQueueStatus() === 'IDLE');
    }

    init() {
        const { device } = dtlSyi.dispatch(getPageStateThunk()).baseConfig;

        this.uploadQueue = [];
        const cleanup = this.initFileInput();
        this.initSortable();

        if (device.type === 'desktop') {
            this.initDragAndDrop();
        }

        return cleanup;
    }

    initFileInput() {
        if (this.fileInput) {
            const onChange = async () => {
                if (this.fileInput?.files) {
                    await this.fileUpload(Array.from(this.fileInput.files));
                    trackEvents('AddImageBegin');
                }
            };
            this.fileInput.addEventListener('change', onChange);
            return () => this.fileInput?.removeEventListener('change', onChange);
        } else {
            return undefined;
        }
    }

    initDragAndDrop() {
        const dropArea = document.querySelector('.add-images') as HTMLDivElement;

        ['dragenter', 'dragleave', 'dragover', 'drop'].forEach((eventName) => {
            dropArea.addEventListener(eventName, (e) => {
                e.preventDefault();
                e.stopPropagation();
            });
        });

        ['dragenter', 'dragover'].forEach((eventName) => {
            dropArea.addEventListener(eventName, () =>
                dtlSyi.dispatch(updateSyi({ isDragging: true }))
            );
        });

        ['dragleave', 'drop'].forEach((eventName) => {
            dropArea.addEventListener(eventName, () =>
                dtlSyi.dispatch(updateSyi({ isDragging: false }))
            );
        });

        dropArea.addEventListener('drop', (e) => this.onFileDrop(e));
    }

    onFileDrop(e) {
        const dt = e.dataTransfer;
        const files: FileList = dt.files;
        const imageFiles: File[] = [...files].filter((file) => this.isImageFile(file));
        this.fileUpload([...imageFiles]);
    }

    isImageFile(file: File) {
        const fileExtension = file.name.split('.').pop()?.toLowerCase();

        return (
            allowedTypes.includes(file.type) ||
            (fileExtension && allowedTypes.some((type) => type.includes(fileExtension)))
        );
    }

    async isAllowedImageType(file: File) {
        const isValidType = this.isImageFile(file);

        if (!isValidType) return false;

        const actualType = await getActualImageType(file);
        trackGA4Event<GA4.UploadImageTypeEvent>({
            event: 'upload_image_type',
            fileType: file.type,
            actualType: actualType ?? '',
        })
        // getActualImageType failed, use the isImageFile
        if (actualType === 'image/heic') return false;
        return true;
    }

    initSortable() {
        this.sortable = new Sortable(this.el, {
            sort: true,
            animation: 200,
            delay: 300,
            delayOnTouchOnly: true,
            draggable: '.js-sortable',
            handle: '.img-wrap',
            forceFallback: true,
            onEnd: () => this.onDropSortable(),
        });
    }

    isImageLimitReached(offset: number) {
        return this.shownImageNumber() + (offset || 0) >= this.options.maximages;
    }

    onDropSortable() {
        // Must get current ordering from the DOM
        const imageIds = this.getDomImageIds();
        const { imageDefs } = dtlSyi.dispatch(getPageStateThunk()).syi;
        this.updateMainImgId();
        dtlSyi.dispatch(
            updateSyi({
                imageDefs: imageIds.map((id) =>
                    imageDefs.find((def) => def.id === id)
                ) as SyiImageDef[],
            })
        );
        this.imageXhr(
            {
                imgIds: imageIds,
            },
            'POST',
            'reorder'
        ).catch(() =>
            dtlSyi.dispatch(
                updateSyi({
                    imageDefs: imageDefs.slice(),
                })
            )
        );

        trackV2('ImageUploadReorder');
    }

    printError(errorMessage: string) {
        // 🚧 originally this error was appended
        dtlSyi.dispatch(
            updateSyiFormError({ elName: 'imageIds[]', errors: [errorMessage], shouldShow: true })
        );
    }

    shownImageNumber() {
        const lis = Array.from(this.el.children);
        // Possibly one additional <li> (li.add-image)
        return lis.filter((el) => el.classList.contains('js-sortable')).length;
    }

    startUpload(item: QueueItem) {
        if (this.multiUpload) {
            this.uploadXhr(item);
        } else {
            this.addToQueue(item);
        }
    }

    trackEvents(startTime: number) {
        const duration = Date.now() - startTime;
        trackGA4Event<GA4.AddImageSuccess>({
            event: 'add_image_success',
            formName: 'syi-form',
            duration,
            accountId: dtlSyi.accountId,
            ...computeListingDetails(),
        });
    }

    stopSpinner(item: QueueItem) {
        const { imageDefs } = dtlSyi.dispatch(getPageStateThunk()).syi;
        dtlSyi.dispatch(
            updateSyi({
                imageDefs: imageDefs.map((def) => {
                    if ('file' in def && def.file === item.formQuery.image) {
                        def.waiting = false;
                        !!def.startTime && this.trackEvents(def.startTime);
                    }
                    return def;
                }),
            })
        );
    }

    updateMainImgId() {
        const mainImgId = dtlSyi.dispatch(getPageStateThunk()).syi.imageDefs[0]?.id ?? 0;
        this.mainHiddenInput.setAttribute('value', `${mainImgId}`);
    }

    async uploadXhr(item: QueueItem) {
        const formData = new FormData();
        const startTime = Date.now();
        formData.append('image', item.formQuery.resizedBlob || item.formQuery.image);
        formData.append('position', `${item.formQuery.position}`);

        try {
            const resp = await fetchOrThrow(
                this.url,
                {
                    method: 'POST',
                    body: formData,
                    credentials: 'include',
                },
                {
                    serviceName: 'seller',
                    path: '/postad/:id/images',
                    method: 'POST',
                }
            ).then((x) => x.json());

            const { baseConfig, page } = dtlSyi.dispatch(getPageStateThunk());
            UploadImageMetrics.registerStageDuration({
                stage: 'upload',
                durationMs: Date.now() - startTime,
                os: baseConfig.device.os,
                device: baseConfig.device.type,
                appViewDevice: page.appViewDevice,
                imageType: item.formQuery.image.type,
                imageSizeBytes: item.formQuery.image.size,
                resizedImageSizeBytes: item.formQuery.resizedBlob?.size ?? item.formQuery.image.size,
                state: 'success',
            });

            this.imageUploadResponseHandler(item, resp);
        } catch (e) {
            this.imageUploadError(item, e);
        }
    }

    /**
     * Extract meaningful error message from various error types
     */
    private getErrorMessage(error: any): string {
        // Handle Error objects
        if (error instanceof Error) {
            return error.message || error.name || 'Unknown error';
        }

        // Handle string errors
        if (typeof error === 'string') {
            return error;
        }

        // Handle DOM events
        if (error && typeof error === 'object' && error.type) {
            return `DOM Event: ${error.type} - ${
                error.target ? error.target.toString() : 'Unknown target'
            }`;
        }

        // Handle objects with message property
        if (error && typeof error === 'object' && error.message) {
            return error.message;
        }

        // Handle objects with toString method
        if (error && typeof error.toString === 'function') {
            const stringified = error.toString();
            // Avoid [object Object] or [object Event]
            if (stringified !== '[object Object]' && stringified !== '[object Event]') {
                return stringified;
            }
        }

        // Handle objects with name property
        if (error && typeof error === 'object' && error.name) {
            return error.name;
        }

        // Handle objects with code property
        if (error && typeof error === 'object' && error.code) {
            return `Error code: ${error.code}`;
        }

        // Fallback: try to extract useful information
        if (error && typeof error === 'object') {
            const keys = Object.keys(error);
            if (keys.length > 0) {
                return `Error object with keys: ${keys.join(', ')}`;
            }
        }

        // Final fallback
        return 'Unknown error occurred';
    }
}

interface QueueItem {
    formQuery: { image: File; resizedBlob?: Blob; position: number };
}

type QueueStatus = 'BUSY' | 'IDLE';
